import { Badge, Group, Progress, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import type { AdminUser } from "../models/adminUser"
import { MAX_2FA_ATTEMPTS } from "../models/adminUser"
import { EmptyValue } from "../utils"

interface Props {
  user: AdminUser
}

function formatRelativeTime(dateStr: string | null): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "agora"
  if (diffMin < 60) return `${diffMin} min atrás`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h atrás`
  const diffDays = Math.floor(diffHrs / 24)
  return `${diffDays}d atrás`
}

export default function SecurityTabContent({ user }: Props) {
  const { t } = useTranslation()

  return (
    <Stack gap="md">
      <Group gap="xl" wrap="nowrap" align="flex-start">
        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUsers.twoFactorAuth")}
          </Text>
          {user.two_fa_type ? (
            <Group gap="xs">
              <Badge
                color={user.two_fa_type === "authenticator" ? "blue" : "teal"}
                variant="light"
                size="sm"
              >
                {user.two_fa_type === "authenticator"
                  ? t("adminUsers.authenticatorApp")
                  : t("adminUsers.emailAuth")}
              </Badge>
            </Group>
          ) : (
            <EmptyValue />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUsers.failed2faAttempts")}
          </Text>
          {user.failed_2fa_attempts > 0 ? (
            <Stack gap={4}>
              <Text
                size="sm"
                c={user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS ? "red" : "orange"}
              >
                {user.failed_2fa_attempts} / {MAX_2FA_ATTEMPTS}
              </Text>
              <Progress
                value={(user.failed_2fa_attempts / MAX_2FA_ATTEMPTS) * 100}
                color={
                  user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS ? "red" : "orange"
                }
                size="sm"
              />
            </Stack>
          ) : (
            <EmptyValue />
          )}
        </div>
      </Group>

      <Group gap="xl" wrap="nowrap" align="flex-start">
        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUsers.lastLogin")}
          </Text>
          {user.last_login_at ? (
            <Stack gap={2}>
              <Text size="sm">{formatRelativeTime(user.last_login_at)}</Text>
              <Text size="xs" c="dimmed">
                IP: {user.last_login_ip ?? <EmptyValue />}
              </Text>
              {user.last_login_browser && (
                <Text size="xs" c="dimmed">
                  {user.last_login_browser}
                  {user.last_login_os ? ` / ${user.last_login_os}` : ""}
                </Text>
              )}
            </Stack>
          ) : (
            <EmptyValue />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUsers.lastLoginAttempt")}
          </Text>
          {user.last_login_attempt_at ? (
            <Text size="sm">
              {formatRelativeTime(user.last_login_attempt_at)}
            </Text>
          ) : (
            <EmptyValue />
          )}
        </div>
      </Group>

      <Group gap="xl" wrap="nowrap" align="flex-start">
        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUsers.memberSince")}
          </Text>
          {user.created_at ? (
            <Text size="sm">
              {new Date(user.created_at).toLocaleDateString("pt-PT", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Text>
          ) : (
            <EmptyValue />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb={2}>
            {t("adminUser.newsletter")}
          </Text>
          <Group gap="xs">
            <Badge
              color={user.daily_newsletter_enabled ? "green" : "gray"}
              variant="light"
              size="sm"
            >
              {t("adminUsers.dailyNewsletter")}
            </Badge>
            <Badge
              color={user.agency_visit_newsletter_enabled ? "green" : "gray"}
              variant="light"
              size="sm"
            >
              {t("adminUsers.agencyVisitNewsletter")}
            </Badge>
          </Group>
        </div>
      </Group>
    </Stack>
  )
}
